/**
 * OTA glue — this app's configuration of @syqel/ota-client (the host runtime lives in the
 * syqel-sdk repo). main.tsx / tvMain.tsx await initOta() BEFORE first render; everything else
 * (Engine, otaModeFactory, badges, checkForUpdatesNow…) imports @syqel/ota-client directly.
 *
 * ── THE BOOT CONTRACT: no network on the first-render path ──────────────────────────────────────
 * The first render needs exactly what is ALREADY verified + cached; it never needs the registry.
 * The client's default boot races a full sync (manifest + .sig, then every new module, serially)
 * against a time cap and renders after whichever settles — on a cold store that is the whole cap of
 * black screen (the iOS "black for several seconds at launch"). So Art boots RENDER-FIRST
 * (ota-client 0.7 `renderFirst`, which replaced this file's own cacheOnly boot + background
 * OtaSync):
 *   1. initOta() resolves as soon as the cached modules are activated (engine-core swap + world
 *      registration) — local only. Render follows immediately.
 *   2. after first paint the client checks + downloads in the BACKGROUND into the offline store
 *      (skipped when the user chose manual updates), badging arrivals NEW as each lands. Nothing
 *      swaps mid-session (an engine swap can't happen under a running Director): what arrives
 *      activates on the NEXT launch — or now, via the manual "check for new worlds" button
 *      (the client's checkForUpdatesNow, which joins a running background sync), which reloads.
 * A fresh install therefore runs the bundled worlds on its first launch and the OTA set from the
 * second; every launch after that boots from cache with zero network on the critical path.
 *
 * The pubkey pins the Ed25519 manifest signature (apps/web/.env.production); a production
 * build without it keeps OTA off entirely — the app runs bundled, exactly as before OTA existed.
 */
import { initOta as initOtaClient } from '@syqel/ota-client'
import { registerFxWorlds } from '../fxWorlds'

const MANIFEST_URL: string = (import.meta.env.VITE_OTA_MANIFEST_URL as string | undefined) ?? '/ota/manifest.json'
const PUBKEY: string | undefined = (import.meta.env.VITE_OTA_PUBKEY as string | undefined) || undefined

/**
 * Marketplace entitlement pubkey (base64url Ed25519) — verifies the platform's signed entitlement
 * token OFFLINE. The seam is INERT today: every world is free, no world carries a non-free access,
 * so wiring the key changes nothing user-visible — it lets the marketplace turn on server-side later
 * with zero further app releases (the whole point of the SDK-0.8 recut).
 */
const ENTITLEMENT_PUBKEY = 'WuhEDK1qMzKbH1vjxO7t8jSXIudeem7cR8oucIMM-gE'

// TODO(marketplace claim handoff — STUBBED for the SDK-0.8 wave, by design):
// obtain the user's Firebase ID token → POST <platform>/v1/claim → setEntitlement(response.token)
// on boot + on every re-auth. Blocked on TWO platform-side facts (verified 2026-07-26):
//  1. the claim Worker (syqel-platform /v1/claim) has no production route yet, and
//  2. it hard-pins aud === 'syqel-visualizer' — Art's dedicated 'syqel-art' Firebase project
//     (firebaseClaim.ts email-link claims) would be REJECTED. Art must adopt syqel-visualizer
//     sign-in first (platform-consolidation work, explicitly not this wave).
// Until then the pubkey below keeps the seam ready; setEntitlement() is never called.

const REQUIRE_SIGNATURE: boolean = import.meta.env.PROD

/**
 * Boot-time OTA pass: activate what is already verified + cached, then hand the registry check to
 * the client's background sync. Never delays first render on anything but local storage. Never
 * throws (the client degrades to bundled). The bundled fx worlds register AFTER it, into whichever
 * engine the cache activated (fxWorlds.ts).
 */
export async function initOta(): Promise<void> {
  await initOtaClient({
    manifestUrl: MANIFEST_URL,
    pubKeyHex: PUBKEY,
    requireSignature: REQUIRE_SIGNATURE,
    entitlementPubKey: ENTITLEMENT_PUBKEY,
    renderFirst: true,
    onBackgroundSync: ({ applied }) => {
      if (applied.length) console.info(`[syqel·ota] ${applied.length} module(s) staged for the next launch`)
    }
  })
  registerFxWorlds()
}
